import React, { useState, useEffect } from 'react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Check, X, ArrowRight, Menu, X as CloseIcon } from 'lucide-react';
import {
  heroData,
  problemData,
  methodData,
  protectionData,
  targetData,
  teamData,
  ctaData,
  logoUrl,
  navLinks
} from '../data/mock';
import '../styles/landing.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${isScrolled ? 'header-scrolled' : ''}`}>
      <div className="container-main flex items-center justify-between py-5">
        <a href="#" className="logo-wrapper">
          <img src={logoUrl} alt="CLEDICI Conciergerie" className="h-12 w-auto" />
        </a>
        
        <nav className="hidden md:flex items-center gap-8">
          {navLinks.map((link) => (
            <a key={link.href} href={link.href} className="nav-link">
              {link.label}
            </a>
          ))}
        </nav>
        
        <div className="hidden md:block">
          <a href="#contact">
            <Button className="btn-primary-gold">
              Diagnostic gratuit
            </Button>
          </a>
        </div>

        <button 
          className="md:hidden text-gold"
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
        >
          {mobileMenuOpen ? <CloseIcon size={24} /> : <Menu size={24} />}
        </button>
      </div>

      {/* Mobile Menu */}
      <div className={`mobile-menu ${mobileMenuOpen ? 'open' : ''}`}>
        <nav className="flex flex-col gap-6 p-8">
          {navLinks.map((link) => (
            <a 
              key={link.href} 
              href={link.href} 
              className="nav-link-mobile"
              onClick={() => setMobileMenuOpen(false)}
            >
              {link.label}
            </a>
          ))}
          <a href="#contact" onClick={() => setMobileMenuOpen(false)}>
            <Button className="btn-primary-gold w-full mt-4">
              Diagnostic gratuit
            </Button>
          </a>
        </nav>
      </div>
    </header>
  );
};

const HeroSection = () => {
  return (
    <section className="hero-section">
      <div className="container-main">
        <div className="hero-grid">
          <div className="hero-content animate-fade-in">
            <h1 className="hero-title">{heroData.title}</h1>
            <p className="hero-subtitle">{heroData.subtitle}</p>
            <p className="hero-description">{heroData.description}</p>
            <a href="#contact">
              <Button className="btn-primary-gold mt-8">
                {heroData.cta}
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </a>
          </div>
          <div className="hero-image-wrapper animate-fade-in-delay">
            <div className="hero-image-container">
              <img 
                src={heroData.image} 
                alt="Équipe CLEDICI" 
                className="hero-image"
              />
              <div className="hero-image-overlay"></div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

const ProblemSection = () => {
  return (
    <section className="section-problem">
      <div className="container-main">
        <div className="problem-content animate-on-scroll">
          <span className="section-tagline">{problemData.tagline}</span>
          <h2 className="section-title-light">{problemData.title}</h2>
          <ul className="problem-list">
            {problemData.problems.map((problem, index) => (
              <li key={index} className="problem-item">
                <span className="problem-bullet"></span>
                {problem}
              </li>
            ))}
          </ul>
          <p className="problem-conclusion">{problemData.conclusion}</p>
        </div>
      </div>
    </section>
  );
};

const MethodSection = () => {
  return (
    <section id="methode" className="section-method">
      <div className="container-main">
        <div className="method-header animate-on-scroll">
          <span className="section-tagline-gold">{methodData.tagline}</span>
          <h2 className="section-title-dark">{methodData.title}</h2>
        </div>
        <div className="method-grid">
          {methodData.steps.map((step, index) => (
            <div key={index} className="method-card animate-on-scroll" style={{ animationDelay: `${index * 0.1}s` }}>
              <span className="method-number">{step.number}</span>
              <h3 className="method-card-title">{step.title}</h3>
              <p className="method-card-description">{step.description}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

const ProtectionSection = () => {
  return (
    <section id="protection" className="section-protection">
      <div className="container-main">
        <div className="protection-grid">
          <div className="protection-image-wrapper animate-on-scroll">
            <img 
              src={protectionData.image} 
              alt="Protection de votre bien" 
              className="protection-image"
            />
          </div>
          <div className="protection-content animate-on-scroll">
            <span className="section-tagline">{protectionData.tagline}</span>
            <h2 className="section-title-light">{protectionData.title}</h2>
            <p className="protection-description">{protectionData.description}</p>
            <ul className="protection-features">
              {protectionData.features.map((feature, index) => (
                <li key={index} className="protection-feature">
                  <Check className="feature-icon" />
                  {feature}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </section>
  );
};

const TargetSection = () => {
  return (
    <section className="section-target">
      <div className="container-main">
        <div className="target-header animate-on-scroll">
          <span className="section-tagline-gold">{targetData.tagline}</span>
          <h2 className="section-title-dark">{targetData.title}</h2>
        </div>
        <div className="target-grid">
          <div className="target-card target-card-for animate-on-scroll">
            <h3 className="target-card-title">{targetData.forYou.title}</h3>
            <ul className="target-list">
              {targetData.forYou.items.map((item, index) => (
                <li key={index} className="target-item target-item-for">
                  <Check className="target-icon-for" />
                  {item}
                </li>
              ))}
            </ul>
          </div>
          <div className="target-card target-card-not animate-on-scroll">
            <h3 className="target-card-title">{targetData.notForYou.title}</h3>
            <ul className="target-list">
              {targetData.notForYou.items.map((item, index) => (
                <li key={index} className="target-item target-item-not">
                  <X className="target-icon-not" />
                  {item}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </section>
  );
};

const TeamSection = () => {
  return (
    <section id="equipe" className="section-team">
      <div className="container-main">
        <div className="team-header animate-on-scroll">
          <span className="section-tagline">{teamData.tagline}</span>
          <h2 className="section-title-light">{teamData.title}</h2>
          <p className="team-description">{teamData.description}</p>
        </div>
        <div className="team-grid">
          {teamData.members.map((member, index) => (
            <div key={index} className="team-card animate-on-scroll" style={{ animationDelay: `${index * 0.15}s` }}>
              <div className="team-image-wrapper">
                <img 
                  src={member.image} 
                  alt={member.name} 
                  className="team-image"
                />
              </div>
              <div className="team-card-content">
                <h3 className="team-name">{member.name}</h3>
                <p className="team-role">{member.role}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

const CTASection = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    propertyType: '',
    city: ''
  });
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);
    
    try {
      const response = await fetch(`${API}/contact`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...formData,
          submittedAt: new Date().toISOString(),
          source: 'CLEDICI Landing Page'
        }),
      });
      
      const result = await response.json();
      
      if (response.ok && result.success) {
        setIsSubmitted(true);
        setFormData({
          name: '',
          email: '',
          phone: '',
          propertyType: '',
          city: ''
        });
      } else {
        throw new Error(result.message || 'Erreur lors de l\'envoi');
      }
    } catch (err) {
      console.error('Form submission error:', err);
      setError('Une erreur est survenue. Veuillez réessayer.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  return (
    <section id="contact" className="section-cta">
      <div className="container-main">
        <div className="cta-grid">
          <div className="cta-content animate-on-scroll">
            <span className="section-tagline-gold">{ctaData.tagline}</span>
            <h2 className="section-title-dark">{ctaData.title}</h2>
            <p className="cta-description">{ctaData.description}</p>
          </div>
          <div className="cta-form-wrapper animate-on-scroll">
            {isSubmitted ? (
              <div className="form-success">
                <Check className="success-icon" />
                <h3>Merci pour votre demande !</h3>
                <p>Nous vous recontactons sous 24h.</p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="cta-form">
                {error && (
                  <div className="form-error">
                    <p>{error}</p>
                  </div>
                )}
                <div className="form-group">
                  <Label htmlFor="name" className="form-label">Votre nom</Label>
                  <Input
                    id="name"
                    name="name"
                    type="text"
                    placeholder="Jean Dupont"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className="form-input"
                    disabled={isLoading}
                  />
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <Label htmlFor="email" className="form-label">Email</Label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      placeholder="jean@exemple.com"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      className="form-input"
                      disabled={isLoading}
                    />
                  </div>
                  <div className="form-group">
                    <Label htmlFor="phone" className="form-label">Téléphone</Label>
                    <Input
                      id="phone"
                      name="phone"
                      type="tel"
                      placeholder="06 12 34 56 78"
                      value={formData.phone}
                      onChange={handleChange}
                      required
                      className="form-input"
                      disabled={isLoading}
                    />
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <Label htmlFor="propertyType" className="form-label">Type de bien</Label>
                    <Select onValueChange={(value) => setFormData({ ...formData, propertyType: value })} disabled={isLoading}>
                      <SelectTrigger className="form-select">
                        <SelectValue placeholder="Sélectionner" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="appartement">Appartement</SelectItem>
                        <SelectItem value="maison">Maison</SelectItem>
                        <SelectItem value="studio">Studio</SelectItem>
                        <SelectItem value="autre">Autre</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="form-group">
                    <Label htmlFor="city" className="form-label">Ville du bien</Label>
                    <Input
                      id="city"
                      name="city"
                      type="text"
                      placeholder="Paris, Lyon..."
                      value={formData.city}
                      onChange={handleChange}
                      required
                      className="form-input"
                      disabled={isLoading}
                    />
                  </div>
                </div>
                <Button type="submit" className="btn-primary-gold w-full mt-6" disabled={isLoading}>
                  {isLoading ? (
                    <>
                      <span className="loading-spinner"></span>
                      Envoi en cours...
                    </>
                  ) : (
                    <>
                      {ctaData.cta}
                      <ArrowRight className="ml-2 h-4 w-4" />
                    </>
                  )}
                </Button>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

const Footer = () => {
  return (
    <footer className="footer">
      <div className="container-main">
        <div className="footer-content">
          <div className="footer-brand">
            <img src={logoUrl} alt="CLEDICI" className="h-10 w-auto mb-4" />
            <p className="footer-tagline">Conciergerie locale haut de gamme</p>
          </div>
          <div className="footer-links">
            <nav className="footer-nav">
              {navLinks.map((link) => (
                <a key={link.href} href={link.href} className="footer-link">
                  {link.label}
                </a>
              ))}
            </nav>
          </div>
          <div className="footer-contact">
            <p className="footer-contact-item">contact@cledici.fr</p>
            <p className="footer-contact-item">01 23 45 67 89</p>
          </div>
        </div>
        <div className="footer-bottom">
          <p>&copy; 2025 CLEDICI Conciergerie. Tous droits réservés.</p>
        </div>
      </div>
    </footer>
  );
};

const LandingPage = () => {
  useEffect(() => {
    const observerOptions = {
      root: null,
      rootMargin: '0px',
      threshold: 0.1
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('animate-visible');
        }
      });
    }, observerOptions);

    document.querySelectorAll('.animate-on-scroll').forEach(el => {
      observer.observe(el);
    });

    return () => observer.disconnect();
  }, []);

  return (
    <div className="landing-page">
      <Header />
      <main>
        <HeroSection />
        <ProblemSection />
        <MethodSection />
        <ProtectionSection />
        <TargetSection />
        <TeamSection />
        <CTASection />
      </main>
      <Footer />
    </div>
  );
};

export default LandingPage;
